/**
 * MessengerContactFilterDialog — 149-cw-contact-filter.
 * Pengaturan siapa saja yang muncul di tab "Kontak" pada Pesan Internal.
 *
 * Mode:
 *  - all       → seluruh user aktif
 *  - divisions → hanya user yg divisinya termasuk dalam daftar terpilih
 *  - users     → hanya user yang dipilih manual (whitelist)
 *
 * Persistensi: PATCH /api/messaging/contact-filter
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, X } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DIVISIONS } from "@/lib/constants";

export default function MessengerContactFilterDialog({ open, onClose, allUsers, currentFilter, onSaved }) {
  const [mode, setMode] = useState("all");
  const [divisions, setDivisions] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(currentFilter?.mode || "all");
    setDivisions([...(currentFilter?.divisions || [])]);
    setUserIds([...(currentFilter?.user_ids || [])]);
    setQ("");
  }, [open, currentFilter]);

  const toggleDiv = (d) => {
    setDivisions((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };
  const toggleUser = (uid) => {
    setUserIds((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  const filteredUsers = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const arr = (allUsers || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (!qq) return arr.slice(0, 500);
    return arr.filter((u) =>
      (u.name || "").toLowerCase().includes(qq) ||
      (u.email || "").toLowerCase().includes(qq) ||
      (u.division || "").toLowerCase().includes(qq),
    ).slice(0, 500);
  }, [q, allUsers]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { mode, divisions, user_ids: userIds };
      const { data } = await api.patch("/messaging/contact-filter", payload);
      toast.success("Filter kontak disimpan");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal simpan filter");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMode("all");
    setDivisions([]);
    setUserIds([]);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="msg-contact-filter-dialog">
        <DialogHeader>
          <DialogTitle>Pengaturan Tampilan Kontak</DialogTitle>
          <DialogDescription>
            Atur siapa saja yang tampil di tab <b>Kontak</b> di panel Pesan Internal. Filter ini hanya berlaku untuk akun Anda.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Mode selector */}
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Mode filter">
            {[
              { k: "all", label: "Semua user" },
              { k: "divisions", label: "Per divisi" },
              { k: "users", label: "Manual" },
            ].map((m) => (
              <button
                key={m.k}
                type="button"
                role="radio"
                aria-checked={mode === m.k}
                onClick={() => setMode(m.k)}
                data-testid={`msg-contact-filter-mode-${m.k}`}
                className={`px-2 py-2 text-xs font-semibold border transition-colors ${
                  mode === m.k
                    ? "bg-[#0B1D3A] text-white border-[#0B1D3A]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {mode === "all" && (
            <div className="text-xs text-slate-600 italic border border-slate-200 bg-slate-50 px-3 py-2">
              Seluruh user aktif di sistem akan tampil (kecuali diri Anda).
            </div>
          )}

          {mode === "divisions" && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Pilih Divisi</div>
              <div className="flex flex-wrap gap-1.5" data-testid="msg-contact-filter-divisions">
                {DIVISIONS.map((d) => {
                  const active = divisions.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDiv(d)}
                      data-testid={`msg-contact-filter-div-${d}`}
                      aria-pressed={active}
                      className={`px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                        active
                          ? "bg-[#FF5A1F] text-white border-[#FF5A1F]"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              {divisions.length === 0 && (
                <div className="text-[11px] italic text-amber-700 mt-1.5">
                  Belum ada divisi dipilih. Daftar Kontak akan kosong hingga Anda memilih minimal satu divisi.
                </div>
              )}
            </div>
          )}

          {mode === "users" && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Pilih User ({userIds.length})</div>
                {userIds.length > 0 && (
                  <button type="button" onClick={() => setUserIds([])} className="text-[10px] text-rose-600 hover:underline" data-testid="msg-contact-filter-users-clear">
                    Kosongkan
                  </button>
                )}
              </div>
              <div className="relative mb-1.5">
                <Search size={12} className="absolute left-2 top-2 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari nama / divisi / email…"
                  data-testid="msg-contact-filter-user-search"
                  className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-300 focus:outline-none focus:border-[#FF5A1F] rounded-none"
                />
              </div>
              <ul className="max-h-56 overflow-y-auto border border-slate-200" data-testid="msg-contact-filter-user-list">
                {filteredUsers.length === 0 ? (
                  <li className="px-2 py-2 text-[11px] italic text-slate-400">Tidak ada user.</li>
                ) : (
                  filteredUsers.map((u) => {
                    const active = userIds.includes(u.id);
                    return (
                      <li
                        key={u.id}
                        onClick={() => toggleUser(u.id)}
                        data-testid={`msg-contact-filter-user-${u.id}`}
                        aria-pressed={active}
                        className={`px-2 py-1.5 cursor-pointer flex items-center gap-2 border-b border-slate-100 last:border-b-0 ${
                          active ? "bg-orange-50" : "hover:bg-slate-50"
                        }`}
                      >
                        <span
                          className={`w-3.5 h-3.5 border flex-shrink-0 ${
                            active ? "bg-[#FF5A1F] border-[#FF5A1F]" : "bg-white border-slate-400"
                          }`}
                          aria-hidden="true"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-[#0B1D3A] truncate">{u.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">{u.division} Lv{u.level} · {u.email}</div>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
              {userIds.length === 0 && (
                <div className="text-[11px] italic text-amber-700 mt-1.5">
                  Belum ada user dipilih. Daftar Kontak akan kosong hingga Anda memilih minimal satu user.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={reset} data-testid="msg-contact-filter-reset" disabled={busy}>
            Reset
          </Button>
          <Button type="button" variant="outline" onClick={onClose} data-testid="msg-contact-filter-cancel" disabled={busy}>
            Batal
          </Button>
          <Button type="button" onClick={save} className="bg-[#FF5A1F] hover:bg-[#e04a13]" data-testid="msg-contact-filter-save" disabled={busy}>
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
